import React, { useState, useEffect, useCallback } from 'react';
import { Check, X, ArrowRight, ArrowUp, ArrowDown, Link2, Clock, RotateCcw, Plus, Calendar, CalendarClock, GripVertical, ListOrdered, ChevronsUp, Inbox, FileText, Highlighter } from 'lucide-react';
import { getAccessToken } from '../services/auth';
import { neonQuery } from '../services/api';

// Today's board, at the top of To-Do > Greg.
//
// LEFT: one list of everything that has to happen today - tasks and calendar events together.
// Greg, 2026-09-24: "let's not split to-do and on the calendar. They're all things I got to get
// done today. So just one big list."
//
// RIGHT: the schedule. Pull as many across as you like, each with a start, a length and a
// done-by: "I might say, okay, for the next two hours, I got four 30-minute tasks. Let me do one
// from 9 to 9:30, one from 9:30 to 10..." Start defaults to now and the length to 30 minutes.
//
// ⚠️ Done work leaves the list for ACCOMPLISHED (under the schedule), with an untick to undo.
//
// ⚠️ "Back to today" and "To backlog" (and dragging a block to the left) LEAVE NO LOG LINE. The first cut logged it
// under "worked on today", where Greg read it as finished. Only Done is recorded.
//
// ⚠️ Nothing here decides ownership or done state - today-board.js reads the source tables and
// the calendar itself. When nothing is set for the day it renders `fallback` (the frog card).

const base = () => (window.location.hostname === 'localhost'
  ? 'http://localhost:8888/.netlify/functions'
  : '/.netlify/functions');

export async function call(action, params = {}) {
  const headers = { 'Content-Type': 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = 'Bearer ' + token;
  const res = await fetch(base() + '/today-board', {
    method: 'POST', headers, body: JSON.stringify({ action, ...params }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) { const e = new Error(data.error || 'today-board ' + res.status); e.status = res.status; throw e; }
  return data;
}

const LENGTHS = [5, 10, 15, 20, 30, 45, 60, 90];
const lengthName = (m) => (m === 60 ? '1 hour' : m === 90 ? '1.5 hours' : m + ' min');
const clock = (iso) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const pad = (n) => String(n).padStart(2, '0');
const toTimeInput = (iso) => { const d = new Date(iso); return pad(d.getHours()) + ':' + pad(d.getMinutes()); };
// Keep the DATE of the time being edited and change only the hours and minutes.
const fromTimeInput = (hhmm, baseIso) => {
  const [h, m] = String(hhmm).split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const d = new Date(baseIso || Date.now());
  d.setHours(h, m, 0, 0);
  return d.toISOString();
};
const minutesBetween = (a, b) => Math.round((new Date(b) - new Date(a)) / 60000);
const longDay = (ymd) => {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
};
const keyOf = (x) => x.srcTable + ' ' + x.srcId;
// A highlighted (priority) row on the left: shaded, below the frog's green.
const HL_BG = '#FFF4C2', HL_LINE = '#E9C46A', HL_ICON = '#B7791F';
// The only board source with notes (work_item_meta, keyed by this table).
const NOTE_SRC = 'greg_weekly_tasks';

const btn = (bg, color, border) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, minHeight: 36, padding: '0 12px', borderRadius: 8,
  fontSize: 13, fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
  background: bg, color, border: '1px solid ' + (border || bg),
});
const iconBtn = { border: 'none', background: 'none', cursor: 'pointer', padding: 4, color: 'var(--muted, #59707E)', display: 'inline-flex' };
const label = { fontSize: 12, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--navy)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 };

// refreshKey: the host page bumps it after putting something on today from its own list.
// dailyItems: today's habits (greg_daily_today rows) - shown as pills to drag into Upcoming.
// onKeys: told which srcTable:srcId keys are on the board, so the task list below can leave
// them out. Greg, 2026-09-29: everything lives in ONE of Everything for today, Upcoming, Tasks.
export default function TodayBoard({ onChanged, fallback = null, refreshKey = 0, dailyItems = [], onKeys, stepCount = null }) {
  const [b, setB] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState('');
  const [linking, setLinking] = useState(null);
  const [moving, setMoving] = useState(null);
  // Which schedule block is being dragged, and where it would land. Held in the parent so the
  // drop line and the dragged block are drawn from one source of truth.
  const [dragId, setDragId] = useState(null);
  const [hover, setHover] = useState(null); // { id, pos: 'before' | 'after' }
  const [dropHot, setDropHot] = useState(false);
  const [leftHot, setLeftHot] = useState(false);
  const [, tick] = useState(0);
  // Task notes (work_item_meta, the same ones the task list below edits). Greg, Nov 3, 2026: the
  // Billy the electrician tile had a note and no way to see it from the board.
  const [notes, setNotes] = useState({});      // { srcId: text } for greg_weekly_tasks
  const [noteOpen, setNoteOpen] = useState(null); // keyOf(item) of the left-list row showing its note

  const load = useCallback(async () => {
    try { setB(await call('board')); setErr(null); }
    catch (e) { if (e.status === 403) setB({ forbidden: true }); else setErr(e.message); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    if (!b || !b.items) return;
    const ids = [...new Set(b.items.concat(b.scheduled).filter(x => x.srcTable === NOTE_SRC).map(x => String(x.srcId)))];
    if (!ids.length) return;
    let live = true;
    neonQuery('work_goals_bulk', { srcTable: NOTE_SRC, ids })
      .then(r => { if (live && r && r.goals) setNotes(r.goals); })
      .catch(() => {});
    return () => { live = false; };
  }, [b]);
  const saveNote = (srcId, text) => {
    setNotes(prev => ({ ...prev, [String(srcId)]: text }));
    neonQuery('work_goal_set', { srcTable: NOTE_SRC, srcId: String(srcId), goal: text }).catch(() => {});
  };
  useEffect(() => {
    if (!onKeys || !b || !b.items) return;
    onKeys(new Set(b.items.map(keyOf).concat(b.scheduled.map(keyOf))));
  }, [b, onKeys]);
  // ⚠️ SCROLL THE PAGE WHILE A BLOCK IS DRAGGED. Greg, 2026-09-25: "when dragging, I get stuck at
  // the top of the page... if I've got many items, I got to go a few at a time." Chrome only
  // auto-scrolls a drag at the very edge of the scrolling element, and on this page that edge
  // sits under the fixed top bar, so it never triggers. This finds the element that actually
  // scrolls and moves it whenever the pointer is within 110px of the top or bottom of the
  // window - faster the closer it gets, and at full speed once it is over the top bar itself.
  const rootRef = React.useRef(null);
  const drag = React.useRef({ y: null, raf: 0 });
  useEffect(() => {
    if (dragId == null) return undefined;
    const st = drag.current;
    const scroller = (() => {
      let el = rootRef.current && rootRef.current.parentElement;
      while (el && el !== document.body) {
        const oy = getComputedStyle(el).overflowY;
        if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 4) return el;
        el = el.parentElement;
      }
      return null; // the window scrolls
    })();
    const onOver = (e) => { st.y = e.clientY; };
    document.addEventListener('dragover', onOver);
    const ZONE = 110, MAX = 28;
    const frame = () => {
      const y = st.y;
      if (y != null) {
        const h = window.innerHeight;
        let dy = 0;
        if (y < ZONE) dy = -Math.min(MAX, Math.ceil(((ZONE - y) / ZONE) * MAX));
        else if (y > h - ZONE) dy = Math.min(MAX, Math.ceil(((y - (h - ZONE)) / ZONE) * MAX));
        if (dy) { if (scroller) scroller.scrollTop += dy; else window.scrollBy(0, dy); }
      }
      st.raf = requestAnimationFrame(frame);
    };
    st.raf = requestAnimationFrame(frame);
    return () => { document.removeEventListener('dragover', onOver); cancelAnimationFrame(st.raf); st.y = null; };
  }, [dragId]);

  // The countdowns are the point of the clock, so they move without a reload.
  useEffect(() => { const t = setInterval(() => tick(n => n + 1), 20000); return () => clearInterval(t); }, []);

  const act = async (action, params, { refreshOthers = false } = {}) => {
    setBusy(true);
    try {
      setB(await call(action, params));
      setErr(null);
      if (refreshOthers && onChanged) onChanged();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  if (!b) return err ? <div style={{ color: '#8A3312', fontSize: 13, marginBottom: 16 }}>{err}</div> : null;
  if (b.forbidden) return fallback;

  if (b.items.length === 0 && b.scheduled.length === 0) {
    return (
      <div style={{ marginBottom: 20 }}>
        <AddBox draft={draft} setDraft={setDraft} busy={busy}
          onAdd={(t) => act('add', { text: t }, { refreshOthers: true })} label="Nothing is set for today yet. Add the first thing:" />
        {fallback}
      </div>
    );
  }

  // Anything already pulled across lives on the right until it is done or put back.
  const onSchedule = new Set(b.scheduled.map(keyOf));
  // Greg, 2026-09-24: "my goal would be to clear everything on the left side, get it into your
  // schedule and do it, and done so it goes down into accomplished." So the left holds only
  // what is still to do; anything done, however it was done, lives under Accomplished.
  const list = b.items.filter(x => !onSchedule.has(keyOf(x)) && !x.done);
  const sessionFor = new Map(b.finished.map(f => [keyOf(f), f]));
  const itemKeys = new Set(b.items.map(keyOf));
  // A habit done from Upcoming has no row on the list, so it comes in from its session.
  // Only while it is still ticked for today - unticking sends it back to the Daily pills.
  const habitDone = new Set(dailyItems.filter(d => d.completed).map(d => 'greg_daily_templates ' + d.id));
  const doneHabits = b.finished.filter(f => !itemKeys.has(keyOf(f)) && (f.srcTable !== 'greg_daily_templates' || habitDone.has(keyOf(f))))
    .map(f => Object.assign({}, f, { done: true }));
  const accomplished = b.items.filter(x => x.done).concat(doneHabits).map(x => {
    const f = sessionFor.get(keyOf(x));
    return Object.assign({}, x, { session: f || null, at: (f && f.endedAt) || x.doneAt || null });
  }).sort((a, c) => (a.at ? new Date(a.at).getTime() : Infinity) - (c.at ? new Date(c.at).getTime() : Infinity));
  // Logged by a chat or by a system that saw it finish (a newsletter going out). Left out when it
  // closed a board item that is already in the list above, so nothing shows twice.
  const doneKeys = new Set(accomplished.map(keyOf));
  const logged = (b.logged || []).filter(l => !(l.srcTable && doneKeys.has(l.srcTable + ' ' + l.srcId)));
  const pct = b.counts.total ? Math.round((b.counts.done / b.counts.total) * 100) : 0;
  const events = b.items.filter(x => x.kind === 'event');

  // A timed meeting comes across at its real time and length; everything else comes across with
  // no length and follows the block above it.
  const pull = (x) => act('start', x.kind === 'event'
    ? Object.assign({ srcTable: 'calendar', srcId: x.srcId, title: x.title },
        x.isAllDay || !x.start || !x.end ? {} : { startedAt: x.start, minutes: minutesBetween(x.start, x.end) })
    : { srcTable: x.srcTable, srcId: x.srcId });
  // Where each block's Start shows. One with no length yet starts when the block above is due
  // done (or now, if that has passed or there is nothing above) - Greg, 2026-09-28: "7:14 done at
  // 7:19, then that one below it should default to 7:19". Once it has a length the server holds
  // its real time, and keeps it chained.
  const shownStart = new Map();
  {
    let cursor = null;
    const nowMs = Date.now();
    for (const s of b.scheduled) {
      const st = new Date(s.startedAt).getTime();
      const en = s.plannedEnd ? new Date(s.plannedEnd).getTime() : null;
      const shown = en != null || !s.startLinked ? st : Math.max(cursor || nowMs, nowMs);
      shownStart.set(s.id, shown);
      cursor = en != null ? en : shown;
    }
  }
  const dragOf = (x) => (e) => { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', JSON.stringify(x)); };
  const SLOT = 'application/x-cpx-slot';
  const lineUp = (ids) => act('reflow', { sessionIds: ids });
  // Put one block before or after another and re-time the schedule in that order. The order IS
  // the plan: tasks run back to back, meetings keep their time. The list is reordered on screen
  // first so it snaps into place instead of waiting on the round trip.
  const moveSlot = (fromId, targetId, pos) => {
    if (fromId == null || fromId === targetId) return;
    const ids = b.scheduled.map(x => x.id).filter(x => x !== fromId);
    let at = targetId == null ? ids.length : ids.indexOf(targetId);
    if (at < 0) at = ids.length;
    if (targetId != null && pos === 'after') at += 1;
    ids.splice(at, 0, fromId);
    const byId = new Map(b.scheduled.map(x => [x.id, x]));
    setB(Object.assign({}, b, { scheduled: ids.map(id => byId.get(id)) }));
    lineUp(ids);
  };
  const nudge = (id, dir) => {
    const ids = b.scheduled.map(x => x.id);
    const i = ids.indexOf(id), j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    moveSlot(id, ids[j], dir < 0 ? 'before' : 'after');
  };
  const endDrag = () => { setDragId(null); setHover(null); setLeftHot(false); };
  const onDrop = (e) => {
    e.preventDefault(); setDropHot(false);
    if (dragId != null) { const id = dragId; endDrag(); moveSlot(id, null, 'after'); return; }
    try { pull(JSON.parse(e.dataTransfer.getData('text/plain'))); } catch (_) { /* not one of ours */ }
  };

  // Reorder within the visible list, then send the WHOLE order (scheduled ones keep their slot).
  const move = (x, dir) => {
    const i = list.findIndex(y => keyOf(y) === keyOf(x));
    const j = i + dir;
    if (i < 0 || (dir !== 'top' && (j < 0 || j >= list.length))) return;
    const next = list.slice();
    if (dir === 'top') next.unshift(next.splice(i, 1)[0]);
    else [next[i], next[j]] = [next[j], next[i]];
    const order = next.concat(b.items.filter(y => onSchedule.has(keyOf(y)) || y.done));
    setB(Object.assign({}, b, { items: order }));
    act('reorder', { keys: order.map(y => ({ srcTable: y.srcTable, srcId: y.srcId })) });
  };

  return (
    <div ref={rootRef} style={{ marginBottom: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--orange)' }}>Today&rsquo;s board</div>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--navy)', marginTop: 2 }}>{longDay(b.date)}</div>
        </div>
        <div style={{ flex: 1, minWidth: 160, maxWidth: 360 }}>
          <div style={{ height: 8, borderRadius: 999, background: '#E6ECF1', overflow: 'hidden' }}>
            <div style={{ width: pct + '%', height: '100%', background: 'var(--navy)', borderRadius: 999 }} />
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted, #59707E)', marginTop: 4 }}>{b.counts.done} of {b.counts.total} done</div>
        </div>
      </div>

      {err && <div style={{ background: '#FDECE6', border: '1px solid #F4C3AE', borderRadius: 10, padding: '9px 13px', marginBottom: 12, fontSize: 13, color: '#8A3312' }}>{err}</div>}
      {!b.calendar.ok && <div style={{ fontSize: 12.5, color: '#8A3312', marginBottom: 10 }}>{b.calendar.error} Your tasks are all here; calendar items are missing until it reads again.</div>}

      {(() => {
        // What is LEFT of today's habits: not ticked, not already in Upcoming.
        const left = dailyItems.filter(d => !d.completed && !onSchedule.has('greg_daily_templates ' + d.id));
        if (!dailyItems.length) return null;
        return (
          <div style={{ border: '1px solid var(--line, #D9E2E8)', background: '#F7F9FB', borderRadius: 12, padding: '10px 14px', marginBottom: 14 }}>
            <div style={{ ...label, marginBottom: left.length ? 8 : 0, justifyContent: 'space-between' }}>
              <span>Daily</span>
              <span style={{ letterSpacing: 0, textTransform: 'none', fontWeight: 700, color: left.length ? 'var(--orange)' : '#276749' }}>
                {left.length ? left.length + ' of ' + dailyItems.length + ' left · drag one into Upcoming' : 'All ' + dailyItems.length + ' done or in Upcoming'}
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {left.map(d => {
                // 10k steps is written by the step sync, never by hand.
                const auto = String(d.text || '').toLowerCase().includes('10k steps');
                const x = { kind: 'daily', srcTable: 'greg_daily_templates', srcId: String(d.id), title: d.text };
                return (
                  <button key={d.id} draggable={!auto} onDragStart={auto ? undefined : dragOf(x)}
                    onClick={() => !auto && pull(x)} disabled={busy || auto}
                    title={auto ? 'Recorded automatically from your step count' : 'Drag into Upcoming, or click to add it'}
                    style={{ padding: '5px 12px', borderRadius: 999, fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit',
                      background: '#fff', border: '1px solid var(--line, #D9E2E8)', color: 'var(--navy)',
                      cursor: auto ? 'default' : 'grab', opacity: auto ? 0.8 : 1 }}>
                    {d.text}
                    {/* How close it is to ticking itself off. Read when the page loads. */}
                    {auto && stepCount != null && (
                      <span style={{ marginLeft: 6, fontWeight: 700, color: 'var(--orange)' }}>
                        {Number(stepCount).toLocaleString()} / 10,000
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })()}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 18, alignItems: 'start' }}>

        {/* ── LEFT: one list. A schedule block dropped anywhere on it goes back to today. ── */}
        <div onDragOver={(e) => { if (dragId != null) { e.preventDefault(); if (!leftHot) setLeftHot(true); } }}
          onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setLeftHot(false); }}
          onDrop={(e) => { if (dragId == null) return; e.preventDefault(); const id = dragId; setLeftHot(false); endDrag(); act('unschedule', { sessionId: id, to: 'today' }); }}
          style={{ borderRadius: 12, outline: leftHot ? '2px dashed var(--orange)' : 'none', outlineOffset: 6, background: leftHot ? '#FFF3ED' : 'transparent' }}>
          <div style={label}>Everything for today{dragId != null && <span style={{ color: 'var(--orange)', letterSpacing: 0, textTransform: 'none', fontWeight: 700 }}>&nbsp;&middot; drop here to put it back</span>}</div>
          <div style={{ display: 'grid', gap: 6 }}>
            {list.map((x, i) => (
              <div key={keyOf(x)}>
                <div draggable={!x.done} onDragStart={dragOf(x)} style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 10,
                  background: x.frog && !x.done ? '#F2FAF4' : x.highlight && !x.done ? HL_BG : 'var(--surface, #fff)',
                  border: x.frog && !x.done ? '2px solid #38A169' : x.highlight && !x.done ? '1px solid ' + HL_LINE : '1px solid var(--line, #D9E2E8)',
                  opacity: x.done ? 0.55 : 1, cursor: x.done ? 'default' : 'grab',
                }}>
                  <input type="checkbox" checked={x.done} disabled={busy}
                    onChange={() => act('toggle', { srcTable: x.srcTable, srcId: x.srcId, done: !x.done }, { refreshOthers: true })}
                    style={{ width: 17, height: 17, accentColor: 'var(--navy)', flexShrink: 0 }} />
                  {x.kind === 'event' && (
                    <span title="On your calendar" style={{ fontFamily: 'monospace', fontSize: 12.5, color: 'var(--navy)', minWidth: 66, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <Calendar size={12} /> {x.isAllDay ? 'All day' : clock(x.start)}
                    </span>
                  )}
                  <span style={{ flex: 1, minWidth: 0 }}>
                    {x.frog && !x.done && <span title="Today's frog (Frog week, above)" style={{ marginRight: 6 }}>🐸</span>}
                    <span style={{ fontSize: 14, fontWeight: x.frog && !x.done ? 700 : 400, textDecoration: x.done ? 'line-through' : 'none' }}>{x.title}</span>
                    {x.frog && !x.done && (
                      <span style={{ marginLeft: 8, fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em', color: '#276749', background: '#DDF2E3', borderRadius: 999, padding: '2px 8px', verticalAlign: '1px', whiteSpace: 'nowrap' }}>
                        EAT THIS FIRST
                      </span>
                    )}
                    {x.carriedFrom && (
                      <span title="On an earlier day's list and not finished, so it carried over"
                        style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: 'var(--orange)', background: '#FFF3ED', borderRadius: 999, padding: '2px 8px', verticalAlign: '1px', whiteSpace: 'nowrap' }}>
                        from {new Date(x.carriedFrom + 'T12:00:00').toLocaleDateString([], { weekday: 'short' })}
                      </span>
                    )}
                    {x.effortMinutes && <span style={{ marginLeft: 8, fontSize: 11.5, color: 'var(--muted, #59707E)' }}><Clock size={11} style={{ verticalAlign: '-1px' }} /> {x.effortMinutes}m</span>}
                    {x.linked && (
                      <span style={{ display: 'block', fontSize: 11.5, color: 'var(--muted, #59707E)', marginTop: 2 }}>
                        <Link2 size={11} style={{ verticalAlign: '-1px' }} /> same as your task &ldquo;{x.linked.title}&rdquo;
                        <button onClick={() => act('unlink', { srcTable: x.linked.srcTable, srcId: x.linked.srcId })}
                          style={{ ...iconBtn, fontSize: 11.5, padding: '0 0 0 6px', textDecoration: 'underline' }}>unlink</button>
                      </span>
                    )}
                  </span>
                  {!x.done && (
                    <button onClick={() => act('highlight', { srcTable: x.srcTable, srcId: x.srcId, on: !x.highlight })}
                      title={x.highlight ? 'Remove the highlight' : 'Highlight as a priority'}
                      style={{ ...iconBtn, color: x.highlight ? HL_ICON : iconBtn.color }}><Highlighter size={14} /></button>
                  )}
                  <button onClick={() => move(x, 'top')} disabled={i === 0} title="Move to top" style={{ ...iconBtn, opacity: i === 0 ? 0.3 : 1 }}><ChevronsUp size={15} /></button>
                  <button onClick={() => move(x, -1)} disabled={i === 0} title="Move up" style={iconBtn}><ArrowUp size={14} /></button>
                  <button onClick={() => move(x, 1)} disabled={i === list.length - 1} title="Move down" style={iconBtn}><ArrowDown size={14} /></button>
                  {x.kind === 'task' && events.length > 0 && (
                    <button onClick={() => setLinking(linking === keyOf(x) ? null : keyOf(x))} title="This is the same thing as a meeting on the calendar" style={iconBtn}><Link2 size={14} /></button>
                  )}
                  {x.kind === 'task' && (
                    <button onClick={() => setMoving(moving === keyOf(x) ? null : keyOf(x))} title="Move to tomorrow or another day" style={iconBtn}><CalendarClock size={14} /></button>
                  )}
                  {x.kind === 'task' && (
                    <button onClick={() => act('remove', { srcTable: x.srcTable, srcId: x.srcId })} title="Not today (the task stays on your list)" style={iconBtn}><X size={14} /></button>
                  )}
                  {x.srcTable === NOTE_SRC && (
                    <button onClick={() => setNoteOpen(noteOpen === keyOf(x) ? null : keyOf(x))}
                      title={notes[String(x.srcId)] ? 'Show the note' : 'Add a note'}
                      style={{ ...iconBtn, color: notes[String(x.srcId)] ? 'var(--orange)' : iconBtn.color }}><FileText size={14} /></button>
                  )}
                  {!x.done && (
                    <button onClick={() => pull(x)} disabled={busy} title="Put it in Upcoming" style={btn('transparent', 'var(--orange)', 'var(--orange)')}>
                      Pull <ArrowRight size={14} />
                    </button>
                  )}
                </div>
                {noteOpen === keyOf(x) && (
                  <NoteBox text={notes[String(x.srcId)] || ''} onSave={(t) => saveNote(x.srcId, t)} onClose={() => setNoteOpen(null)} />
                )}
                {moving === keyOf(x) && (
                  <MoveDay today={b.date} busy={busy}
                    onMove={(date) => { setMoving(null); act('defer', { srcTable: x.srcTable, srcId: x.srcId, date }, { refreshOthers: true }); }}
                    onCancel={() => setMoving(null)} />
                )}
                {linking === keyOf(x) && (
                  <div style={{ padding: '8px 12px', fontSize: 12.5, color: 'var(--muted, #59707E)' }}>
                    Same thing as{' '}
                    <select defaultValue="" onChange={(e) => { if (e.target.value) { act('link', { srcTable: x.srcTable, srcId: x.srcId, calendarEventId: e.target.value }); setLinking(null); } }}
                      style={{ fontSize: 13, padding: '4px 6px', borderRadius: 6, border: '1px solid var(--line, #D9E2E8)', fontFamily: 'inherit' }}>
                      <option value="">pick the meeting...</option>
                      {events.filter(e => !e.linked).map(e => (
                        <option key={e.srcId} value={e.srcId}>{e.isAllDay ? 'All day' : clock(e.start)} {e.title}</option>
                      ))}
                    </select>
                    {' '}so it shows once.
                  </div>
                )}
              </div>
            ))}
            {list.length === 0 && <div style={{ fontSize: 13.5, color: 'var(--navy)', fontWeight: 600 }}>Left side cleared. Everything is scheduled or done.</div>}
          </div>
          <div style={{ marginTop: 10 }}>
            <AddBox draft={draft} setDraft={setDraft} busy={busy} onAdd={(t) => act('add', { text: t }, { refreshOthers: true })} />
          </div>
        </div>

        {/* ── RIGHT: the schedule ── */}
        <div style={{ display: 'grid', gap: 16 }}>
          <div onDragOver={(e) => { e.preventDefault(); setDropHot(true); }} onDragLeave={() => setDropHot(false)} onDrop={onDrop}>
            <div style={{ ...label, color: 'var(--orange)', justifyContent: 'space-between' }}>
              <span>Upcoming</span>
              {b.scheduled.filter(x => x.srcTable !== 'calendar').length > 1 && (
                <button onClick={() => lineUp(b.scheduled.map(x => x.id))} disabled={busy}
                  title="Run the tasks back to back in this order, keeping each one's length"
                  style={{ ...btn('transparent', 'var(--navy)', 'var(--line, #D9E2E8)'), minHeight: 30, fontSize: 12, letterSpacing: 0, textTransform: 'none' }}>
                  <ListOrdered size={13} /> Line them up
                </button>
              )}
            </div>
            <div style={{ display: 'grid', gap: 10 }}>
              {b.scheduled.map(s => (
                <Slot key={s.id} s={s} busy={busy} today={b.date} slotType={SLOT} shownStart={shownStart.get(s.id)}
                  note={s.srcTable === NOTE_SRC ? (notes[String(s.srcId)] || '') : null}
                  onSaveNote={(t) => saveNote(s.srcId, t)}
                  dragActive={dragId != null} dragging={dragId === s.id}
                  indicator={hover && hover.id === s.id && dragId !== s.id ? hover.pos : null}
                  onGrab={() => setDragId(s.id)} onEndDrag={endDrag}
                  onHover={(pos) => { if (!hover || hover.id !== s.id || hover.pos !== pos) setHover({ id: s.id, pos }); }}
                  onDropHere={(pos) => { const id = dragId; endDrag(); moveSlot(id, s.id, pos); }}
                  isFirst={s.id === b.scheduled[0].id} isLast={s.id === b.scheduled[b.scheduled.length - 1].id}
                  onUp={() => nudge(s.id, -1)} onDown={() => nudge(s.id, 1)}
                  onTop={() => moveSlot(s.id, b.scheduled[0].id, 'before')}
                  onMove={s.srcTable === 'calendar' ? null : (date) => act('defer', { srcTable: s.srcTable, srcId: s.srcId, date }, { refreshOthers: true })}
                  onTimes={(p) => act('times', Object.assign({ sessionId: s.id }, p))}
                  onDone={() => act('finish', { sessionId: s.id }, { refreshOthers: true })}
                  onBack={() => act('unschedule', { sessionId: s.id, to: 'today' })}
                  backLabel={s.srcTable === 'greg_daily_templates' ? 'Back to Daily' : 'Back to today'}
                  onBacklog={s.srcTable === 'calendar' || s.srcTable === 'greg_daily_templates' ? null : () => act('unschedule', { sessionId: s.id, to: 'backlog' }, { refreshOthers: true })} />
              ))}
            </div>
            <div style={{
              marginTop: b.scheduled.length ? 10 : 0, borderRadius: 12, padding: '14px 16px', fontSize: 13.5, lineHeight: 1.5,
              color: 'var(--muted, #59707E)', background: dropHot ? '#FFF3ED' : '#F7F9FB',
              border: '2px dashed ' + (dropHot ? 'var(--orange)' : 'var(--line, #D9E2E8)'),
            }}>
              {b.scheduled.length === 0
                ? 'Pull things across to plan your day. The first starts now and each one after starts when the one above is done. Pick how long and it tells you when to be done by.'
                : 'Drop another here, or hit Pull.'}
            </div>
          </div>

          <div>
            <div style={label}><Check size={14} /> Accomplished{accomplished.length + logged.length ? ' (' + (accomplished.length + logged.length) + ')' : ''}</div>
            {accomplished.length + logged.length === 0 && <div style={{ fontSize: 13, color: 'var(--muted, #59707E)' }}>Nothing yet. Tick something off and it lands here.</div>}
            <div style={{ display: 'grid', gap: 5 }}>
              {logged.map(l => (
                <div key={'log-' + l.id} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13.5, padding: '7px 10px', borderRadius: 10, background: '#F4F8F5', border: '1px solid #DCE9E0' }}>
                  <Check size={15} color="var(--navy)" style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1, minWidth: 0, color: 'var(--ink, #0B1F2A)' }}>
                    {l.text}
                    {l.forWhom && <span style={{ color: 'var(--muted, #59707E)' }}> &middot; for {l.forWhom}</span>}
                  </span>
                  <span title={l.source === 'chat' ? 'Logged by a Claude chat when it shipped' : 'Logged when the system saw it happen'}
                    style={{ fontSize: 11, fontWeight: 700, color: 'var(--navy)', background: '#E6ECF1', borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap' }}>
                    {l.source === 'chat' ? 'via Claude' : l.source === 'dotdigital' ? 'sent' : l.source}
                  </span>
                  <span style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap', color: 'var(--muted, #59707E)' }}>{clock(l.at)}</span>
                </div>
              ))}
              {accomplished.map(x => {
                const f = x.session;
                const took = f ? minutesBetween(f.startedAt, f.endedAt) : null;
                const plan = f && f.plannedEnd ? minutesBetween(f.startedAt, f.plannedEnd) : null;
                return (
                  <div key={keyOf(x)} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13.5, padding: '7px 10px', borderRadius: 10, background: '#F4F8F5', border: '1px solid #DCE9E0' }}>
                    {/* Unticking sends it back to the left - a misclick costs one click, not a hunt. */}
                    <input type="checkbox" checked disabled={busy} title="Not done after all - put it back on the list"
                      onChange={() => act('toggle', { srcTable: x.srcTable, srcId: x.srcId, done: false }, { refreshOthers: true })}
                      style={{ width: 16, height: 16, accentColor: 'var(--navy)', flexShrink: 0 }} />
                    <span style={{ flex: 1, minWidth: 0, color: 'var(--ink, #0B1F2A)' }}>{x.title}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap', color: plan !== null && took > plan ? 'var(--orange)' : 'var(--muted, #59707E)' }}>
                      {f ? clock(f.startedAt) + ' to ' + clock(f.endedAt) + ' · ' + took + 'm' + (plan !== null ? ' of ' + plan : '')
                         : x.at ? 'done ' + clock(x.at) : 'done'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// One block on the schedule: start, length, done by, and where it stands against the clock.
function Slot({ s, busy, today, onTimes, onDone, onBack, backLabel = 'Back to today', onBacklog, onMove, slotType, shownStart,
  dragActive, dragging, indicator, onGrab, onEndDrag, onHover, onDropHere, isFirst, isLast, onUp, onDown, onTop, note, onSaveNote }) {
  const [moving, setMoving] = useState(false);
  const [noteShown, setNoteShown] = useState(false);
  const card = React.useRef(null);
  const now = Date.now();
  // No length yet = no Done by and no clock running; the block just waits for "how long".
  const planned = !!s.plannedEnd;
  const start = shownStart != null ? shownStart : new Date(s.startedAt).getTime();
  const startIso = new Date(start).toISOString();
  const end = planned ? new Date(s.plannedEnd).getTime() : null;
  const length = planned ? Math.round((end - start) / 60000) : null;
  const running = planned && now >= start && now < end;
  const over = planned && now >= end;
  const status = !planned ? 'how long?'
    : now < start ? 'starts in ' + Math.round((start - now) / 60000) + ' min'
    : over ? Math.round((now - end) / 60000) + ' min over' : Math.round((end - now) / 60000) + ' min left';
  const pct = planned ? Math.min(100, Math.max(0, Math.round(((now - start) / (end - start)) * 100))) : 0;
  // Picking a length or a Done by pins the start that was showing (now, or the block above's
  // done-by) without unlinking it. Typing a Start or hitting Now unlinks it.
  const follows = s.startLinked && !isFirst;
  // Top half of the block drops it above, bottom half below - the line shows which.
  const half = (e) => {
    const r = card.current ? card.current.getBoundingClientRect() : null;
    return r && e.clientY > r.top + r.height / 2 ? 'after' : 'before';
  };

  return (
    <div ref={card}
      onDragOver={(e) => { if (dragActive) { e.preventDefault(); e.stopPropagation(); onHover(half(e)); } }}
      onDrop={(e) => { if (dragActive) { e.preventDefault(); e.stopPropagation(); onDropHere(half(e)); } }}
      style={{
      position: 'relative', borderRadius: 12, padding: '8px 12px', background: 'var(--surface, #fff)', overflow: 'hidden',
      border: (running || over ? '2px solid var(--orange)' : '1px solid var(--line, #D9E2E8)'),
      opacity: dragging ? 0.4 : 1,
      boxShadow: indicator === 'before' ? '0 -5px 0 0 var(--orange)' : indicator === 'after' ? '0 5px 0 0 var(--orange)' : 'none',
    }}>
      {/* ⚠️ THE WHOLE TITLE BAR IS THE HANDLE AND IT IS ALWAYS draggable. The first cut switched
          draggable on in onMouseDown, and Chrome had already read the press as a text selection -
          Greg's screenshot showed the title highlighted blue and nothing moving. user-select:none
          keeps the title from being grabbed as text. The time boxes and dropdown are below this
          bar, so they are still ordinary controls. */}
      <div draggable
        onDragStart={(e) => {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData(slotType, String(s.id));
          if (card.current && e.dataTransfer.setDragImage) e.dataTransfer.setDragImage(card.current, 24, 20);
          onGrab();
        }}
        onDragEnd={onEndDrag}
        title="Drag to move it; the schedule re-times in the new order"
        style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'grab', userSelect: 'none', WebkitUserSelect: 'none' }}>
        <span style={{ color: 'var(--muted, #59707E)', display: 'inline-flex' }}><GripVertical size={16} /></span>
        <div style={{ flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: 700, color: 'var(--navy)', lineHeight: 1.25 }}>
          {s.title}
          {s.carriedFrom && (
            <span title="Pulled on an earlier day and not finished, so it carried over"
              style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: 'var(--orange)', background: '#FFF3ED', borderRadius: 999, padding: '2px 8px', verticalAlign: '2px' }}>
              from {new Date(s.carriedFrom + 'T12:00:00').toLocaleDateString([], { weekday: 'short' })}
            </span>
          )}
        </div>
        <span style={{ fontSize: 12.5, fontWeight: 700, whiteSpace: 'nowrap', color: over ? 'var(--orange)' : running ? 'var(--navy)' : 'var(--muted, #59707E)' }}>{status}</span>
        {note != null && (
          <button onClick={() => setNoteShown(!noteShown)} title={note ? 'Show the note' : 'Add a note'} draggable={false}
            style={{ ...iconBtn, color: note ? 'var(--orange)' : iconBtn.color }}><FileText size={15} /></button>
        )}
        <button onClick={onTop} disabled={busy || isFirst} title="Move to first" draggable={false}
          style={{ ...iconBtn, opacity: isFirst ? 0.3 : 1 }}><ChevronsUp size={15} /></button>
        <button onClick={onUp} disabled={busy || isFirst} title="Move earlier" draggable={false}
          style={{ ...iconBtn, opacity: isFirst ? 0.3 : 1 }}><ArrowUp size={15} /></button>
        <button onClick={onDown} disabled={busy || isLast} title="Move later" draggable={false}
          style={{ ...iconBtn, opacity: isLast ? 0.3 : 1 }}><ArrowDown size={15} /></button>
      </div>
      {/* ⚠️ ONE ROW, NO STACKED LABELS. Greg, 2026-10-01: "tighten up the upcoming tiles. Make them
          half the height... I'm going to be able to see more things." Start / for / done by sit
          inline, the three ways back are icons with tooltips, and the progress bar is a thin strip
          on the bottom edge of the card instead of its own line. */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6, alignItems: 'center' }}>
        <TimeField label={follows ? '↳ Start' : 'Start'} value={toTimeInput(startIso)}
          hint={follows ? 'Starts when the one above is due done. Type a time or hit Now to set it yourself.' : undefined}
          onCommit={(v) => { const iso = fromTimeInput(v, startIso); if (iso) onTimes({ startedAt: iso, unlink: true }); }} />
        <button onClick={() => onTimes({ startedAt: new Date().toISOString(), unlink: true })} disabled={busy}
          title="Start it right now" style={{ ...btn('transparent', 'var(--orange)', 'var(--orange)'), ...slim }}>Now</button>
        <label style={fieldLabel}>
          for
          <select value={!planned ? '' : LENGTHS.includes(length) ? String(length) : 'custom'} disabled={busy}
            onChange={(e) => { const v = e.target.value; if (v && v !== 'custom') onTimes({ startedAt: startIso, minutes: Number(v) }); }}
            style={{ ...fieldInput, color: planned ? 'var(--navy)' : 'var(--orange)', fontWeight: planned ? 400 : 700 }}>
            {!planned && <option value="">How long?</option>}
            {LENGTHS.map(m => <option key={m} value={m}>{lengthName(m)}</option>)}
            {planned && !LENGTHS.includes(length) && <option value="custom">{length} min</option>}
          </select>
        </label>
        <TimeField label="done by" value={planned ? toTimeInput(new Date(end).toISOString()) : ''}
          onCommit={(v) => { const iso = fromTimeInput(v, startIso); if (iso) onTimes({ startedAt: startIso, plannedEnd: iso }); }} />
        <span style={{ flex: 1 }} />
        <button onClick={onDone} disabled={busy} style={{ ...btn('var(--navy)', '#fff'), ...slim }}><Check size={14} /> Done</button>
        {/* Three places a thing can live: backlog (the task list below), today (the left), and
            the schedule. Both ways back leave no log line. You can also drag it to the left. */}
        <button onClick={onBack} disabled={busy} title={backLabel + ' (nothing logged)'} aria-label={backLabel} style={slimIcon}><RotateCcw size={15} /></button>
        {onBacklog && (
          <button onClick={onBacklog} disabled={busy} title="To backlog: off today entirely, it stays open on your task list below" aria-label="To backlog" style={slimIcon}><Inbox size={15} /></button>
        )}
        {onMove && (
          <button onClick={() => setMoving(!moving)} disabled={busy} title="Another day: move to tomorrow or later" aria-label="Another day" style={slimIcon}><CalendarClock size={15} /></button>
        )}
      </div>
      {(running || over) && (
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 3, background: '#E6ECF1' }}>
          <div style={{ width: pct + '%', height: '100%', background: over ? 'var(--orange)' : 'var(--navy)' }} />
        </div>
      )}
      {noteShown && note != null && <NoteBox text={note} onSave={onSaveNote} onClose={() => setNoteShown(false)} />}
      {moving && onMove && <MoveDay today={today} busy={busy} onMove={(d) => { setMoving(false); onMove(d); }} onCancel={() => setMoving(false)} />}
    </div>
  );
}

// The note under a board item: read it, or edit it in place. Saves when the box loses focus.
function NoteBox({ text, onSave, onClose }) {
  const [draft, setDraft] = useState(text);
  useEffect(() => { setDraft(text); }, [text]);
  return (
    <div style={{ marginTop: 6, padding: '8px 12px', borderRadius: 10, background: '#F7F9FB', border: '1px solid var(--line, #D9E2E8)' }}>
      <textarea value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Add a note"
        onBlur={() => { if (draft !== text) onSave(draft); }}
        rows={Math.min(10, Math.max(3, draft.split('\n').length + 1))}
        style={{ width: '100%', boxSizing: 'border-box', fontSize: 13.5, lineHeight: 1.45, fontFamily: 'inherit', color: 'var(--navy)', border: 'none', background: 'transparent', resize: 'vertical', outline: 'none' }} />
      <div style={{ textAlign: 'right' }}>
        <button onClick={() => { if (draft !== text) onSave(draft); onClose(); }} style={{ ...btn('transparent', 'var(--navy)', 'var(--line, #D9E2E8)'), ...slim }}>Close</button>
      </div>
    </div>
  );
}

const fieldLabel = { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, color: 'var(--muted, #59707E)', whiteSpace: 'nowrap' };
const slim = { minHeight: 30, padding: '0 10px', fontSize: 12.5 };
const slimIcon = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 8, cursor: 'pointer', background: 'transparent', color: 'var(--muted, #59707E)', border: '1px solid var(--line, #D9E2E8)' };
const fieldInput = { fontSize: 13.5, height: 30, boxSizing: 'border-box', padding: '2px 6px', borderRadius: 8, border: '1px solid var(--line, #D9E2E8)', fontFamily: 'inherit', color: 'var(--navy)', background: '#fff' };

function TimeField({ label: text, value, onCommit, hint }) {
  const [v, setV] = useState(value);
  // A start that shows "now" moves with the clock; don't yank it out from under a half-typed edit.
  const editing = React.useRef(false);
  useEffect(() => { if (!editing.current) setV(value); }, [value]);
  return (
    <label style={fieldLabel} title={hint}>
      {text}
      <input type="time" value={v}
        onFocus={() => { editing.current = true; }}
        onChange={(e) => setV(e.target.value)}
        onBlur={() => { editing.current = false; if (v && v !== value) onCommit(v); }}
        style={fieldInput} />
    </label>
  );
}

// Move a task off today: Tomorrow in one click, or any later day. Tomorrow is computed from the
// board's own date (Eastern), never the browser clock, so it cannot land on the wrong day at night.
function MoveDay({ today, busy, onMove, onCancel }) {
  const [y, m, d] = today.split('-').map(Number);
  const iso = (n) => { const t = new Date(Date.UTC(y, m - 1, d + n)); return t.toISOString().slice(0, 10); };
  const tomorrow = iso(1);
  const name = (ymd) => { const [a, b2, c] = ymd.split('-').map(Number); return new Date(a, b2 - 1, c).toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' }); };
  const [pick, setPick] = useState(iso(2));
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '8px 12px', fontSize: 12.5, color: 'var(--muted, #59707E)' }}>
      Move to
      <button onClick={() => onMove(tomorrow)} disabled={busy} style={btn('var(--navy)', '#fff')}>Tomorrow ({name(tomorrow)})</button>
      or
      <input type="date" value={pick} min={tomorrow} onChange={(e) => setPick(e.target.value)} className="cpx-touch"
        style={{ fontSize: 13, padding: '5px 7px', borderRadius: 8, border: '1px solid var(--line, #D9E2E8)', fontFamily: 'inherit' }} />
      <button onClick={() => pick && pick > today && onMove(pick)} disabled={busy || !pick || pick <= today} style={btn('transparent', 'var(--navy)', 'var(--navy)')}>Move</button>
      <button onClick={onCancel} style={iconBtn} title="Cancel"><X size={14} /></button>
    </div>
  );
}

function AddBox({ draft, setDraft, busy, onAdd, label: text }) {
  return (
    <form onSubmit={(e) => { e.preventDefault(); const t = draft.trim(); if (!t || busy) return; setDraft(''); onAdd(t); }}>
      {text && <div style={{ fontSize: 13, color: 'var(--muted, #59707E)', marginBottom: 6 }}>{text}</div>}
      <div style={{ display: 'flex', gap: 8 }}>
        <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="+ Add something to today..." className="cpx-touch"
          style={{ flex: 1, padding: '9px 12px', fontSize: 14, border: '1px solid var(--line, #D9E2E8)', borderRadius: 10, fontFamily: 'inherit' }} />
        <button type="submit" disabled={busy || !draft.trim()} style={btn('var(--navy)', '#fff')}><Plus size={14} /> Add</button>
      </div>
    </form>
  );
}
